import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './Header.css';

/**
 * Header Component
 * Displays the Constellation KG brand lockup with clickable navigation.
 *
 * Public users see Login (top-right) + "Not a community member? Join us here." subtext.
 * Authenticated users see the Logout button.
 */

// Inline SVG for the light logo mark (from public/logo-ckg-light.svg)
const LogoMark = () => (
  <svg
    className="header-logo-mark"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 160 160"
    width="52"
    height="52"
    aria-hidden="true"
  >
    <rect x="2.5" y="2.5" width="155" height="155" rx="22" fill="white" stroke="#5C899D" strokeWidth="5"/>
    <text x="80" y="126" fontFamily="Helvetica, Arial, sans-serif" fontSize="140" fontWeight="400" fill="#5C899D" textAnchor="middle">C</text>
    <g transform="translate(53, 46) scale(0.60)">
      <polygon fill="#5C899D" points="91.25,50 54.903,47.234 67.887,32.114 52.765,45.096 50,8.75 47.234,45.096 32.113,32.114 45.097,47.234 8.75,50 45.096,52.765 32.114,67.886 47.234,54.903 50,91.25 52.765,54.902 67.886,67.886 54.903,52.765"/>
    </g>
  </svg>
);

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', {
        message: error.message,
        timestamp: new Date().toISOString()
      });
    }
  };

  const handleTitleClick = () => {
    if (user) {
      navigate('/my-knowledge-entries');
    } else {
      navigate('/');
    }
  };

  return (
    <header className="app-header">
      <div
        className="header-lockup header-clickable"
        onClick={handleTitleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleTitleClick();
          }
        }}
      >
        <LogoMark />
        <div className="header-text">
          <span className="header-title">
            Constellation KG<sup className="header-tm">&trade;</sup>
          </span>
          <span className="header-tagline">Explore your knowledge</span>
        </div>
      </div>

      {user ? (
        <button
          className="logout-button"
          onClick={handleLogout}
          aria-label="Logout"
        >
          Logout
        </button>
      ) : (
        <div className="header-cta">
          <button
            className="login-button"
            onClick={() => navigate('/login')}
            aria-label="Login"
          >
            Login
          </button>
          <p className="header-cta-subtext">
            Not a community member?{' '}
            <button
              type="button"
              className="header-cta-link"
              onClick={() => navigate('/join-waitlist')}
            >
              Join us here.
            </button>
          </p>
        </div>
      )}
    </header>
  );
};

export default Header;
